import React, { useEffect, useState, useCallback } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { User, Trash2, Plus, CheckCircle, AlertCircle, Clock, ChevronDown, Save, HelpCircle, Edit2, Users } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";

// Create fresh copy of empty parent
const createFreshParent = (createEmptyParent) => {
    const newParent = createEmptyParent();
    return { ...newParent };
};

const ParentForm = ({ parents, setParents, updateParent, mode, createEmptyParent, loading, preview, setPreview, onSave }) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);


    // Ensure parents is always an array
    const safeParents = Array.isArray(parents) ? parents : [];

    // Initialize expanded state for parent cards
    useEffect(() => {
        const initialExpandedState = {};
        safeParents.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeParents.length]);

    const toggleCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addParent = useCallback(() => {
        if (safeParents.length >= 2) {
            showErrorToast("Only father & mother are allowed");
            return;
        }
        if (setParents && createEmptyParent) {
            setParents(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newParent = createFreshParent(createEmptyParent);
                const newArray = [...currentArray, newParent];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setParents, createEmptyParent, safeParents.length]);

    const removeParent = useCallback((index) => {
        if (setParents) {
            setParents(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, i) => i !== index);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < index) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > index) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setParents]);

    const handleUpdateParent = useCallback((index, field, value) => {
        if (updateParent) {
            updateParent(index, field, value);
        }
    }, [updateParent]);

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const validateForm = () => {
        const newErrors = {};
        safeParents.forEach((parent, i) => {
            if (!parent.relation) newErrors[`relation_${i}`] = "Relation is required";
            if (!parent.name) newErrors[`name_${i}`] = "First name is required";
            if (!parent.surname) newErrors[`surname_${i}`] = "Surname is required";
            if (!parent.dateOfBirth) newErrors[`dateOfBirth_${i}`] = "Date of birth is required";
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in view mode with preview
    if ((mode === "view" || preview) && !isEditing) {
        return (
            <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <Users size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Parents Details</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Information for visa processing and family composition</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Parents information verified and approved</p>
                        </div>
                    )}

                    <div className="space-y-4 sm:space-y-5">
                        {safeParents.filter(p => p.name).length === 0 ? (
                            <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                <Users size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No parent records added</p>
                            </div>
                        ) : (
                            safeParents.filter(p => p.name).map((p, i) => (
                                <div key={i} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:shadow-md transition-all duration-300">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm sm:text-base capitalize">{p.relation}: {p.name} {p.surname}</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                        <Preview label="Full Name" value={`${p.name} ${p.surname}`} />
                                        <Preview label="Date of Birth" value={formatDate(p.dateOfBirth)} />
                                        <Preview label="Date of Death" value={formatDate(p.dateOfDeath)} />
                                        <Preview label="Place of Birth" value={p.placeOfBirth} />
                                        <Preview label="In Canada" value={p.isInCanada ? "Yes" : "No"} />
                                        <div className="sm:col-span-2 lg:col-span-3">
                                            <Preview label="Current Address" value={p.currentAddress} />
                                        </div>
                                        {p.isInCanada && (
                                            <div className="sm:col-span-2 lg:col-span-3">
                                                <Preview label="Canada Address" value={p.canadaAddress} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Parents Information
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving parents information...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <Users size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Parents Details</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Add information about your parents</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {safeParents.length === 0 ? (
                        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <Users size={40} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No parent records added</p>
                            <button
                                type="button"
                                onClick={addParent}
                                className="mt-3 text-blue-600 text-sm font-medium flex items-center gap-1 mx-auto"
                            >
                                <Plus size={16} /> Add your first parent
                            </button>
                        </div>
                    ) : (
                        safeParents.map((p, i) => (
                            <div key={i} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                                {/* Card Header - Clickable on mobile */}
                                <div
                                    className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
                                    onClick={() => toggleCard(i)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base capitalize">
                                                {p.relation ? `${p.relation}` : "New Parent"}
                                            </h3>
                                            {p.name && (
                                                <p className="text-[10px] sm:text-xs text-gray-500">
                                                    {p.name} {p.surname}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            className="md:hidden text-gray-400"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCard(i);
                                            }}
                                        >
                                            <ChevronDown size={18} className={`transition-transform ${expandedCards[i] ? 'rotate-180' : ''}`} />
                                        </button>
                                        {safeParents.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeParent(i);
                                                }}
                                                className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className={`
                                    transition-all duration-300 overflow-hidden
                                    ${expandedCards[i] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                `}>
                                    <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                            <div className="md:col-span-2">
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
                                                    Relation <span className="text-rose-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        value={p.relation}
                                                        onChange={(e) => handleUpdateParent(i, "relation", e.target.value)}
                                                        className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm
                                                            ${localErrors[`relation_${i}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                    >
                                                        <option value="">Select Relation</option>
                                                        <option value="father">Father</option>
                                                        <option value="mother">Mother</option>
                                                    </select>
                                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                </div>
                                                {localErrors[`relation_${i}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`relation_${i}`]}</p>
                                                )}
                                            </div>

                                            <Input
                                                label="First Name"
                                                value={p.name}
                                                onChange={(v) => handleUpdateParent(i, "name", v)}
                                                error={localErrors[`name_${i}`]}
                                                required
                                            />
                                            <Input
                                                label="Surname"
                                                value={p.surname}
                                                onChange={(v) => handleUpdateParent(i, "surname", v)}
                                                error={localErrors[`surname_${i}`]}
                                                required
                                            />

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
                                                    Date of Birth <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={p.dateOfBirth ? new Date(p.dateOfBirth).toISOString().split('T')[0] : ''}
                                                    onChange={(e) => handleUpdateParent(i, "dateOfBirth", e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`dateOfBirth_${i}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`dateOfBirth_${i}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`dateOfBirth_${i}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">Date of Death </label>
                                                <input
                                                    type="date"
                                                    value={p.dateOfDeath ? new Date(p.dateOfDeath).toISOString().split('T')[0] : ''}
                                                    onChange={(e) => handleUpdateParent(i, "dateOfDeath", e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    min={p.dateOfBirth ? new Date(p.dateOfBirth).toISOString().split('T')[0] : undefined}
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm"
                                                />
                                            </div>

                                            <Input
                                                label="Place of Birth"
                                                value={p.placeOfBirth}
                                                onChange={(v) => handleUpdateParent(i, "placeOfBirth", v)}
                                                placeholder="City, Country"
                                            />

                                            <div className="md:col-span-2">
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">Current Address</label>
                                                <textarea
                                                    rows="2"
                                                    value={p.currentAddress}
                                                    onChange={(e) => handleUpdateParent(i, "currentAddress", e.target.value)}
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm resize-none"
                                                    placeholder="Full residential address"
                                                />
                                            </div>

                                            <div className="md:col-span-2 p-3 sm:p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                                                <label className="flex items-center gap-3 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        checked={p.isInCanada}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            handleUpdateParent(i, "isInCanada", checked);
                                                            if (!checked) handleUpdateParent(i, "canadaAddress", "");
                                                        }}
                                                    />
                                                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Currently residing in Canada?</span>
                                                </label>

                                                {p.isInCanada && (
                                                    <div className="mt-3 animate-in slide-in-from-top-2">
                                                        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">Canada Address</label>
                                                        <input
                                                            value={p.canadaAddress}
                                                            onChange={(e) => handleUpdateParent(i, "canadaAddress", e.target.value)}
                                                            className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm"
                                                            placeholder="Street, City, Province, Postal Code"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            onClick={addParent}
                            disabled={safeParents.length >= 2}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all
                                ${safeParents.length >= 2
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600"
                                }`}
                        >
                            <Plus size={18} /> Add {safeParents.length === 0 ? "Father/Mother" : safeParents.length === 1 ? "Other Parent" : "Parent"}
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
};

// Input Component with error handling
const Input = ({ label, value, onChange, required, placeholder, error }) => (
    <div>
        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1">
            {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                ${error ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
        />
        {error && <p className="text-rose-500 text-[10px] mt-1">{error}</p>}
    </div>
);

// Preview Component
const Preview = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</span>
        <span className="text-gray-800 font-semibold text-sm break-words">{value || "—"}</span>
    </div>
);

export default ParentForm;